// src/pages/calidad/AccionesCorrectivas.jsx
// KPI 5.3 Acciones Correctivas — basado en el F-SGI-07 (Listado de acciones correctivas).
//
//  - Fecha límite = fecha de asignación de AC + 28 días naturales (la calcula el sistema).
//    Con reapertura, la fecha límite es la fecha de reapertura.
//  - Avance = suma de 6 etapas, cada una con su tope (15/15/15/25/20/10).
//  - Estatus: Cerrado (100%) · Por validar (todo menos la efectividad) ·
//    Atrasada / En tiempo (según la fecha límite).
//  - % en tiempo = (cerradas o por validar dentro de su fecha + abiertas que no han vencido) ÷ total.

import { Fragment, useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { FiDownload, FiPlus, FiChevronDown, FiChevronUp, FiInfo, FiX } from 'react-icons/fi';
import api from '../../services/api';
import s from '../mantenimiento/estilos';

const BASE = '/api/calidad/acciones-correctivas';
const COLOR = { verde: '#3E7C5A', amarillo: '#B8862E', rojo: '#B23A48', morado: '#4A1E5C', gris: '#8A7690', azul: '#4F7CA8', lila: '#A98BB8' };
const ESTADO_UI = {
  Atrasada: { color: COLOR.rojo, fondo: '#F7E5E7' },
  'En tiempo': { color: COLOR.azul, fondo: '#E3EDF7' },
  'Por validar': { color: '#6E4F80', fondo: '#EFE9F3' },
  Cerrado: { color: COLOR.verde, fondo: '#E3F0E8' },
};
const ETAPAS = [
  ['avance_correcciones', 'Aplicación de correcciones', 15],
  ['avance_causa_raiz', 'Identificación de causa raíz', 15],
  ['avance_plan_accion', 'Definición del plan de acción', 15],
  ['avance_implementacion', 'Implementación del plan de acción', 25],
  ['avance_verificacion_cierre', 'Verificación del cierre', 20],
  ['avance_efectividad', 'Verificación de la efectividad de las acciones', 10],
];
const DIAS_PLAZO = 28;

// ─── Helpers ───
const num = (v) => (v === null || v === undefined || v === '' ? null : parseFloat(v));
const pad = (n) => String(n).padStart(2, '0');
const aISO = (d) => {
  if (!d) return '';
  const x = new Date(d);
  return `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}`;
};
const hoyISO = () => aISO(new Date());
const masDias = (iso, dias) => {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + dias);
  return aISO(d);
};
const fmtFecha = (d) => (d ? new Date(String(d).length === 10 ? d + 'T00:00:00' : d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: '2-digit' }) : '—');

const VACIO = {
  numero_ac: '', fecha_apertura: hoyISO(), origen: '', tipo: 'NC', area: '', descripcion_hallazgo: '',
  responsable: '', responsable_seguimiento: '', documentada_fsgi08: true, observaciones: '',
};

const Tag = ({ children, color = COLOR.gris, fondo = '#F0E9E0', title }) => (
  <span title={title} style={{ ...x.tag, color, background: fondo }}>{children}</span>
);

function BarraAvance({ valor }) {
  const v = Math.max(0, Math.min(100, num(valor) || 0));
  const color = v >= 100 ? COLOR.verde : v >= 90 ? '#6E4F80' : COLOR.lila;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 110 }}>
      <div style={x.barraFondo}><div style={{ ...x.barraLlena, width: `${v}%`, background: color }} /></div>
      <span style={{ fontSize: 12, fontWeight: 700, minWidth: 34, textAlign: 'right' }}>{Math.round(v)}%</span>
    </div>
  );
}

export default function AccionesCorrectivas() {
  const [resumen, setResumen] = useState(null);
  const [lista, setLista] = useState([]);
  const [catalogos, setCatalogos] = useState({ origenes: [], tipos: [], areas: [], responsables: [], siguienteFolio: '' });
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [mensaje, setMensaje] = useState(null);
  const [filtros, setFiltros] = useState({ estado: '', origen: '', area: '', busqueda: '' });
  const [abierta, setAbierta] = useState(null); // id expandido
  const [nueva, setNueva] = useState(null); // formulario de alta (o null)
  const [guardando, setGuardando] = useState(false);

  async function cargarLista(f = filtros) {
    const params = {};
    Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
    const res = await api.get(BASE, { params });
    setLista(res.data.data);
  }

  async function cargarTodo(f = filtros) {
    const [r, c] = await Promise.all([api.get(`${BASE}/resumen`), api.get(`${BASE}/catalogos`)]);
    setResumen(r.data.data);
    setCatalogos(c.data.data);
    await cargarLista(f);
  }

  useEffect(() => {
    (async () => {
      try {
        setCargando(true);
        await cargarTodo();
        setError(null);
      } catch (err) {
        console.error(err);
        setError(err.response?.status === 404
          ? 'La ruta de Acciones Correctivas no está disponible en el servidor (revisar que esté montada en index.js).'
          : 'No se pudieron cargar las acciones correctivas.');
      } finally {
        setCargando(false);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function aplicarFiltro(cambio) {
    const f = { ...filtros, ...cambio };
    setFiltros(f);
    cargarLista(f).catch(() => setMensaje({ tipo: 'error', texto: 'No se pudo filtrar.' }));
  }

  const errorTexto = (err, def) =>
    err.response?.status === 403 ? 'No tienes permiso de captura en Calidad.' : err.response?.data?.error || def;

  async function crear() {
    if (!nueva.fecha_apertura || !nueva.descripcion_hallazgo.trim() || !nueva.origen) {
      setMensaje({ tipo: 'error', texto: 'Fecha de asignación, origen y descripción del problema son obligatorios.' });
      return;
    }
    try {
      setGuardando(true);
      const res = await api.post(BASE, nueva);
      setMensaje({ tipo: 'ok', texto: `Acción correctiva ${res.data.numero_ac} creada.` });
      setNueva(null);
      await cargarTodo();
      setAbierta(res.data.id);
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: errorTexto(err, 'No se pudo crear.') });
    } finally {
      setGuardando(false);
    }
  }

  async function guardar(id, cambios) {
    try {
      setGuardando(true);
      await api.put(`${BASE}/${id}`, cambios);
      setMensaje({ tipo: 'ok', texto: 'Cambios guardados.' });
      const [r] = await Promise.all([api.get(`${BASE}/resumen`), cargarLista()]);
      setResumen(r.data.data);
      return true;
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: errorTexto(err, 'No se pudieron guardar los cambios.') });
      return false;
    } finally {
      setGuardando(false);
    }
  }

  function excel() {
    const filas = lista.map((a) => ({
      'No.': a.numero_ac,
      'Descripción del problema': a.descripcion_hallazgo,
      'Área': a.area || '',
      '% Avance': num(a.avance),
      Origen: a.origen || '',
      Tipo: a.tipo || '',
      'Fecha de asignación': fmtFecha(a.fecha_apertura),
      'Fecha de asignación + 28 días': fmtFecha(a.fecha_compromiso),
      Reapertura: a.reapertura ? 'Sí' : '',
      'Fecha de reapertura': a.reapertura ? fmtFecha(a.fecha_reapertura) : '',
      'Fecha límite': fmtFecha(a.fecha_limite),
      'Fecha real de cierre': fmtFecha(a.fecha_cierre),
      '¿Documentada en F-SGI-08?': a.documentada_fsgi08 ? 'Sí' : 'No',
      Status: a.estado,
      '¿En tiempo?': a.en_tiempo ? 'Sí' : 'No',
      'Responsable de la AC': a.responsable || '',
      'Responsable de seguimiento': a.responsable_seguimiento || '',
      ...Object.fromEntries(ETAPAS.map(([k, label, tope]) => [`${label} (${tope}%)`, num(a[k])])),
      'Validación de efectividad': fmtFecha(a.fecha_validacion_efectividad),
      Observaciones: a.observaciones || '',
    }));
    const ws = XLSX.utils.json_to_sheet(filas);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Acciones correctivas');
    XLSX.writeFile(wb, `Acciones_correctivas_${hoyISO()}.xlsx`);
  }

  if (cargando) return <div style={s.paginaContenedor}>Cargando...</div>;
  if (error) return <div style={{ ...s.paginaContenedor, color: COLOR.rojo }}>{error}</div>;

  const pct = num(resumen?.pct_en_tiempo);
  const colorPct = pct === null ? COLOR.gris : pct >= 90 ? COLOR.verde : pct >= 75 ? COLOR.amarillo : COLOR.rojo;

  return (
    <div style={s.paginaContenedor} className="contenedor-captura">
      <datalist id="ac-areas">{catalogos.areas.map((a) => <option key={a} value={a} />)}</datalist>
      <datalist id="ac-responsables">{catalogos.responsables.map((a) => <option key={a} value={a} />)}</datalist>

      <div style={{ ...s.headerFila, flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
        <div>
          <h1 style={{ ...s.tituloPagina, marginBottom: 2 }}>Acciones Correctivas</h1>
          <div style={{ fontSize: 12, color: COLOR.gris }}>F-SGI-07 · Listado de acciones correctivas</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={excel} disabled={lista.length === 0} style={{ ...x.botonSecundario, display: 'flex', alignItems: 'center', gap: 6 }}><FiDownload /> Excel</button>
          <button
            onClick={() => setNueva(nueva ? null : { ...VACIO, numero_ac: catalogos.siguienteFolio, fecha_apertura: hoyISO() })}
            style={{ ...s.botonGuardarChico, display: 'flex', alignItems: 'center', gap: 6 }}
          >
            {nueva ? <><FiX /> Cancelar</> : <><FiPlus /> Nueva acción correctiva</>}
          </button>
        </div>
      </div>

      {mensaje && (
        <div style={{ ...s.mensaje, color: mensaje.tipo === 'ok' ? COLOR.verde : COLOR.rojo, display: 'flex', justifyContent: 'space-between' }}>
          {mensaje.texto}
          <button style={s.linkChico} onClick={() => setMensaje(null)}>cerrar</button>
        </div>
      )}

      {resumen && (
        <div style={s.kgrid}>
          <div style={{ ...s.kcard, borderTop: `3px solid ${colorPct}` }}>
            <div style={s.kLbl}>% en tiempo</div>
            <div style={{ ...s.kVal, fontSize: 32, color: colorPct }}>{pct === null ? '—' : `${pct}%`}</div>
            <div style={s.kSub}>{resumen.en_tiempo} de {resumen.total} acciones · meta 90%</div>
          </div>
          <TarjetaConteo titulo="Atrasadas" valor={resumen.atrasadas} color={COLOR.rojo} sub="Vencieron sin llegar a 'Por validar'" onClick={() => aplicarFiltro({ estado: 'Atrasada' })} />
          <TarjetaConteo titulo="En proceso (en tiempo)" valor={resumen.abiertas_en_tiempo} color={COLOR.azul} sub="Abiertas que no han vencido" onClick={() => aplicarFiltro({ estado: 'En tiempo' })} />
          <TarjetaConteo titulo="Por validar" valor={resumen.por_validar} color="#6E4F80" sub="Falta verificar la efectividad" onClick={() => aplicarFiltro({ estado: 'Por validar' })} />
          <TarjetaConteo titulo="Cerradas" valor={resumen.cerradas} color={COLOR.verde} sub={`Avance promedio: ${num(resumen.avance_promedio) ?? '—'}%`} onClick={() => aplicarFiltro({ estado: 'Cerrado' })} />
        </div>
      )}

      {nueva && (
        <div style={{ ...s.tarjeta, borderTop: `3px solid ${COLOR.morado}` }}>
          <div style={s.seccionTitulo}>Nueva acción correctiva</div>
          <FormDatosGenerales valores={nueva} onCambio={setNueva} catalogos={catalogos} esNueva />
          <button onClick={crear} disabled={guardando} style={{ ...s.botonGuardarChico, marginTop: 12 }}>
            {guardando ? 'Guardando...' : 'Crear acción correctiva'}
          </button>
        </div>
      )}

      <div style={s.filtrosBar}>
        <div style={s.filtroGrupo}>
          <span style={s.filtroLabel}>Estatus</span>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {[['', 'Todas'], ['Atrasada', 'Atrasadas'], ['En tiempo', 'En tiempo'], ['Por validar', 'Por validar'], ['Cerrado', 'Cerradas']].map(([k, label]) => (
              <button key={k} onClick={() => aplicarFiltro({ estado: k })} style={{ ...s.subTabBtn, ...(filtros.estado === k ? s.subTabBtnActivo : {}) }}>{label}</button>
            ))}
          </div>
        </div>
        <div style={s.filtroGrupo}>
          <span style={s.filtroLabel}>Origen</span>
          <select style={s.filtroInput} value={filtros.origen} onChange={(e) => aplicarFiltro({ origen: e.target.value })}>
            <option value="">Todos</option>
            {catalogos.origenes.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
        <div style={s.filtroGrupo}>
          <span style={s.filtroLabel}>Buscar</span>
          <input
            type="text"
            placeholder="No., problema, responsable…"
            style={{ ...s.filtroInput, minWidth: 200 }}
            value={filtros.busqueda}
            onChange={(e) => setFiltros({ ...filtros, busqueda: e.target.value })}
            onKeyDown={(e) => { if (e.key === 'Enter') aplicarFiltro({}); }}
          />
        </div>
        <button style={s.botonBuscar2} onClick={() => aplicarFiltro({})}>Buscar</button>
        <button style={s.linkChico} onClick={() => aplicarFiltro({ estado: '', origen: '', area: '', busqueda: '' })}>Limpiar</button>
      </div>

      <div style={s.tablaScroll}>
        <table style={s.tabla}>
          <thead>
            <tr>
              <th style={s.th}></th>
              <th style={s.th}>No.</th>
              <th style={s.th}>Descripción del problema</th>
              <th style={s.th}>Área</th>
              <th style={s.th}>Origen</th>
              <th style={s.th}>Responsable</th>
              <th style={s.th}>Fecha límite</th>
              <th style={s.th}>Avance</th>
              <th style={s.th}>Status</th>
            </tr>
          </thead>
          <tbody>
            {lista.length === 0 && (
              <tr><td colSpan={9} style={{ ...s.td, color: COLOR.gris, padding: 16 }}>No hay acciones correctivas con estos filtros.</td></tr>
            )}
            {lista.map((a) => {
              const ui = ESTADO_UI[a.estado] || { color: COLOR.gris, fondo: '#F0E9E0' };
              const esAbierta = abierta === a.id;
              const dias = num(a.dias_para_limite);
              return (
                <Fragment key={a.id}>
                  <tr onClick={() => setAbierta(esAbierta ? null : a.id)} style={{ cursor: 'pointer', ...(a.estado === 'Atrasada' ? { background: '#FDF6F6' } : {}) }}>
                    <td style={s.td}>{esAbierta ? <FiChevronUp /> : <FiChevronDown />}</td>
                    <td style={{ ...s.td, fontWeight: 700, color: COLOR.morado }}>{a.numero_ac || '—'}</td>
                    <td style={{ ...s.td, whiteSpace: 'normal', minWidth: 240 }}>{a.descripcion_hallazgo}</td>
                    <td style={{ ...s.td, whiteSpace: 'normal' }}>{a.area || '—'}</td>
                    <td style={{ ...s.td, whiteSpace: 'normal' }}>
                      {a.origen || '—'}
                      {a.tipo && <div style={x.sub}>{a.tipo}</div>}
                    </td>
                    <td style={{ ...s.td, whiteSpace: 'normal' }}>
                      {a.responsable || '—'}
                      {a.responsable_seguimiento && <div style={x.sub}>Seg.: {a.responsable_seguimiento}</div>}
                    </td>
                    <td style={s.td}>
                      {fmtFecha(a.fecha_limite)}
                      {a.reapertura && <div><Tag color={COLOR.amarillo} fondo="#F9F0DD" title={`Original: ${fmtFecha(a.fecha_compromiso)}`}>reapertura</Tag></div>}
                      {dias !== null && (
                        <div style={{ ...x.sub, color: dias < 0 ? COLOR.rojo : COLOR.gris }}>
                          {dias < 0 ? `${Math.abs(dias)} días de atraso` : dias === 0 ? 'vence hoy' : `faltan ${dias} días`}
                        </div>
                      )}
                    </td>
                    <td style={s.td}><BarraAvance valor={a.avance} /></td>
                    <td style={s.td}>
                      <Tag color={ui.color} fondo={ui.fondo}>{a.estado}</Tag>
                      {['Por validar', 'Cerrado'].includes(a.estado) && !a.en_tiempo && <div><Tag color={COLOR.rojo} fondo="#F7E5E7">fuera de tiempo</Tag></div>}
                    </td>
                  </tr>
                  {esAbierta && (
                    <tr>
                      <td colSpan={9} style={x.celdaEditor}>
                        <EditorAC ac={a} catalogos={catalogos} guardando={guardando} onGuardar={(cambios) => guardar(a.id, cambios)} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ ...s.seccionKpiBloque, marginTop: 16 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <FiInfo style={{ color: COLOR.morado, marginTop: 2, flexShrink: 0 }} />
          <div style={s.seccionKpiFormula}>
            <strong style={{ color: '#2A1830' }}>Cómo se calcula.</strong> La fecha límite es la fecha de asignación de la AC + {DIAS_PLAZO} días naturales;
            si hay reapertura, la fecha límite es la fecha de reapertura. El avance es la suma de las 6 etapas, cada una con su máximo
            (correcciones 15%, causa raíz 15%, plan de acción 15%, implementación 25%, verificación del cierre 20%, efectividad 10%).
            Con las primeras 5 etapas completas la AC queda <strong>Por validar</strong>; con las 6, <strong>Cerrada</strong>.
            <br />
            <strong style={{ color: '#2A1830' }}>% en tiempo</strong> = (cerradas o por validar antes de su fecha límite + abiertas que todavía no vencen) ÷ total.
          </div>
        </div>
      </div>
    </div>
  );
}

function TarjetaConteo({ titulo, valor, color, sub, onClick }) {
  return (
    <div style={{ ...s.kcard, borderTop: `3px solid ${color}`, cursor: 'pointer' }} onClick={onClick} title="Filtrar la lista">
      <div style={s.kLbl}>{titulo}</div>
      <div style={{ ...s.kVal, fontSize: 30, color }}>{valor ?? 0}</div>
      <div style={s.kSub}>{sub}</div>
    </div>
  );
}

// ─── Datos generales (alta y edición) ───
function FormDatosGenerales({ valores, onCambio, catalogos, esNueva }) {
  const set = (campo) => (e) => onCambio({ ...valores, [campo]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  const limite = esNueva ? masDias(valores.fecha_apertura, DIAS_PLAZO) : null;
  return (
    <div style={x.grid}>
      <label style={x.label}>No. de AC
        <input style={s.input} value={valores.numero_ac || ''} onChange={set('numero_ac')} placeholder="automático" />
      </label>
      <label style={x.label}>Fecha de asignación de AC
        <input type="date" style={s.input} value={valores.fecha_apertura || ''} onChange={set('fecha_apertura')} />
        {esNueva && <span style={x.ayuda}>Fecha límite (+{DIAS_PLAZO} días): <strong>{fmtFecha(limite)}</strong></span>}
      </label>
      <label style={x.label}>Origen *
        <select style={s.input} value={valores.origen || ''} onChange={set('origen')}>
          <option value="">Selecciona…</option>
          {catalogos.origenes.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </label>
      <label style={x.label}>Tipo (Origen 2)
        <select style={s.input} value={valores.tipo || ''} onChange={set('tipo')}>
          <option value="">—</option>
          {catalogos.tipos.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </label>
      <label style={x.label}>Área
        <input style={s.input} list="ac-areas" value={valores.area || ''} onChange={set('area')} />
      </label>
      <label style={x.label}>Responsable de la AC
        <input style={s.input} list="ac-responsables" value={valores.responsable || ''} onChange={set('responsable')} />
      </label>
      <label style={x.label}>Responsable de seguimiento
        <input style={s.input} list="ac-responsables" value={valores.responsable_seguimiento || ''} onChange={set('responsable_seguimiento')} />
      </label>
      <label style={{ ...x.label, flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 18 }}>
        <input type="checkbox" checked={!!valores.documentada_fsgi08} onChange={set('documentada_fsgi08')} />
        ¿Documentada en F-SGI-08?
      </label>
      <label style={{ ...x.label, gridColumn: '1 / -1' }}>Descripción del problema *
        <textarea style={x.textarea} value={valores.descripcion_hallazgo || ''} onChange={set('descripcion_hallazgo')} />
      </label>
      <label style={{ ...x.label, gridColumn: '1 / -1' }}>Observaciones
        <textarea style={x.textarea} value={valores.observaciones || ''} onChange={set('observaciones')} />
      </label>
    </div>
  );
}

// ─── Editor de una AC ───
const CAMPOS_EDITABLES = ['numero_ac', 'fecha_apertura', 'origen', 'tipo', 'area', 'descripcion_hallazgo', 'responsable',
  'responsable_seguimiento', 'documentada_fsgi08', 'observaciones', 'causa_raiz', 'accion_correctiva', 'evidencia_cierre',
  'reapertura', 'fecha_reapertura', 'fecha_validacion_efectividad', ...ETAPAS.map(([k]) => k)];
const CAMPOS_FECHA = ['fecha_apertura', 'fecha_reapertura', 'fecha_validacion_efectividad'];

function valoresIniciales(ac) {
  const v = {};
  CAMPOS_EDITABLES.forEach((c) => {
    if (CAMPOS_FECHA.includes(c)) v[c] = aISO(ac[c]);
    else if (c.startsWith('avance_')) v[c] = num(ac[c]) ?? 0;
    else if (typeof ac[c] === 'boolean') v[c] = ac[c];
    else v[c] = ac[c] ?? '';
  });
  return v;
}

function EditorAC({ ac, catalogos, guardando, onGuardar }) {
  const inicial = useMemo(() => valoresIniciales(ac), [ac]);
  const [v, setV] = useState(inicial);
  useEffect(() => { setV(inicial); }, [inicial]);

  const cambios = Object.fromEntries(CAMPOS_EDITABLES.filter((c) => v[c] !== inicial[c]).map((c) => [c, v[c]]));
  const hayCambios = Object.keys(cambios).length > 0;
  const avance = ETAPAS.reduce((suma, [k]) => suma + (num(v[k]) || 0), 0);
  const set = (campo) => (e) => setV({ ...v, [campo]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  function setEtapa(k, tope, valor) {
    if (valor === '') return setV({ ...v, [k]: '' });
    const n = Math.max(0, Math.min(tope, Number(valor)));
    setV({ ...v, [k]: Number.isNaN(n) ? 0 : n });
  }

  async function handleGuardar() {
    const c = { ...cambios };
    ETAPAS.forEach(([k]) => { if (k in c) c[k] = num(c[k]) || 0; });
    const ok = await onGuardar(c);
    if (!ok) setV(v);
  }

  return (
    <div>
      <div style={x.seccion}>Avance por etapas <span style={{ fontWeight: 400, textTransform: 'none' }}>— {Math.round(avance)}% total</span></div>
      <div style={x.etapas}>
        {ETAPAS.map(([k, label, tope]) => {
          const val = num(v[k]) || 0;
          const completa = val >= tope;
          return (
            <div key={k} style={{ ...x.etapa, borderColor: completa ? '#BFDCC9' : '#E6DDD2', background: completa ? '#F3FAF5' : '#fff' }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#2A1830', minHeight: 32 }}>{label}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input
                  type="number" min="0" max={tope} step="1"
                  style={{ ...s.inputChico, width: 64 }}
                  value={v[k]}
                  onChange={(e) => setEtapa(k, tope, e.target.value)}
                  aria-label={`${label}, máximo ${tope}%`}
                />
                <span style={{ fontSize: 12, color: COLOR.gris }}>de {tope}%</span>
                {!completa && <button style={s.linkChico} onClick={() => setEtapa(k, tope, tope)}>completar</button>}
              </div>
              <div style={x.barraFondo}><div style={{ ...x.barraLlena, width: `${(val / tope) * 100}%`, background: completa ? COLOR.verde : COLOR.lila }} /></div>
            </div>
          );
        })}
      </div>
      {ac.fechas_estimadas && (
        <div style={{ ...x.sub, marginTop: 6 }}>
          Esta AC viene de la carga inicial del Excel: la fecha en que llegó a "Por validar" o "Cerrado" no se conocía y se registró como su fecha límite.
        </div>
      )}

      <div style={x.seccion}>Fecha límite y reapertura</div>
      <div style={x.grid}>
        <div style={x.label}>Fecha de asignación + {DIAS_PLAZO} días
          <div style={{ fontSize: 13, color: '#2A1830', paddingTop: 6 }}>{fmtFecha(ac.fecha_compromiso)}</div>
        </div>
        <label style={{ ...x.label, flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 18 }}>
          <input type="checkbox" checked={!!v.reapertura} onChange={set('reapertura')} />
          Reapertura
        </label>
        {v.reapertura && (
          <label style={x.label}>Fecha de reapertura (nueva fecha límite)
            <input type="date" style={s.input} value={v.fecha_reapertura || ''} onChange={set('fecha_reapertura')} />
          </label>
        )}
        <label style={x.label}>Validación de efectividad programada
          <input type="date" style={s.input} value={v.fecha_validacion_efectividad || ''} onChange={set('fecha_validacion_efectividad')} />
        </label>
      </div>

      <div style={x.seccion}>Datos generales</div>
      <FormDatosGenerales valores={v} onCambio={setV} catalogos={catalogos} />

      <div style={x.seccion}>Seguimiento</div>
      <div style={x.grid}>
        <label style={{ ...x.label, gridColumn: '1 / -1' }}>Causa raíz
          <textarea style={x.textarea} value={v.causa_raiz || ''} onChange={set('causa_raiz')} />
        </label>
        <label style={{ ...x.label, gridColumn: '1 / -1' }}>Acción correctiva / plan de acción
          <textarea style={x.textarea} value={v.accion_correctiva || ''} onChange={set('accion_correctiva')} />
        </label>
        <label style={{ ...x.label, gridColumn: '1 / -1' }}>Evidencia de cierre
          <textarea style={x.textarea} value={v.evidencia_cierre || ''} onChange={set('evidencia_cierre')} />
        </label>
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 12 }}>
        <button onClick={handleGuardar} disabled={!hayCambios || guardando} style={{ ...s.botonGuardarChico, opacity: hayCambios ? 1 : 0.5 }}>
          {guardando ? 'Guardando...' : 'Guardar cambios'}
        </button>
        {hayCambios && <button style={s.linkChico} onClick={() => setV(inicial)}>descartar</button>}
        {ac.fecha_cierre && <span style={x.sub}>Cerrada el {fmtFecha(ac.fecha_cierre)}</span>}
        {!ac.fecha_cierre && ac.fecha_por_validar && <span style={x.sub}>Por validar desde el {fmtFecha(ac.fecha_por_validar)}</span>}
      </div>
    </div>
  );
}

const x = {
  tag: { display: 'inline-flex', alignItems: 'center', fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 10, marginTop: 2, whiteSpace: 'nowrap' },
  sub: { fontSize: 10.5, color: '#8A7690' },
  barraFondo: { flex: 1, height: 8, background: '#F0E9E0', borderRadius: 6, overflow: 'hidden', minWidth: 60 },
  barraLlena: { height: '100%', borderRadius: 6 },
  celdaEditor: { padding: '16px 20px 20px 40px', background: '#FAF6F0', borderBottom: '1px solid #F0E9E0' },
  seccion: { fontSize: 11, fontWeight: 700, color: '#6E4F80', textTransform: 'uppercase', letterSpacing: 0.5, margin: '16px 0 8px' },
  etapas: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 },
  etapa: { border: '1px solid', borderRadius: 10, padding: 10, display: 'flex', flexDirection: 'column', gap: 6 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 },
  label: { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, color: '#8A7690', fontWeight: 600 },
  ayuda: { fontSize: 11, color: '#6E4F80', fontWeight: 400 },
  textarea: { padding: '7px 9px', fontSize: 13, border: '1px solid #E6DDD2', borderRadius: 7, fontFamily: 'Inter, sans-serif', minHeight: 54, resize: 'vertical' },
  botonSecundario: { padding: '6px 14px', fontSize: 12, fontWeight: 600, color: '#4A1E5C', background: '#EFE9F3', border: '1px solid #A98BB8', borderRadius: 6, cursor: 'pointer' },
};